import { useState, type KeyboardEvent } from "react";
import { SearchIcon, SparklesIcon, XIcon } from "lucide-react";

import { Column, LoadingIndicator, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { MAX_DEFAULT_SEARCH_KEYWORDS, MAX_JOB_KEYWORDS } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { JobKeyword } from "@/types";

// Keyword editor for the job form. Every keyword highlights matches on candidate
// profiles; the ones toggled on additionally seed Search V2, so the search
// toggle is the only per-keyword decision a staffer makes here.
export const JobKeywordsField = ({
  isGenerating,
  keywords,
  onChange,
  onGenerate,
}: {
  isGenerating: boolean;
  keywords: JobKeyword[];
  onChange: (keywords: JobKeyword[]) => void;
  onGenerate?: () => void;
}) => {
  /* React Hooks */
  const [keywordInput, setKeywordInput] = useState("");

  /* Helpers */
  const searchKeywordCount = keywords.filter(
    (keyword) => keyword.isDefaultSearchKeyword,
  ).length;
  const isAtKeywordLimit = keywords.length >= MAX_JOB_KEYWORDS;
  const isAtSearchKeywordLimit =
    searchKeywordCount >= MAX_DEFAULT_SEARCH_KEYWORDS;

  const getToggleTooltip = (keyword: JobKeyword) => {
    if (keyword.isDefaultSearchKeyword) {
      return "Searched by default — click to use for highlighting only";
    }
    if (isAtSearchKeywordLimit) {
      return `Max ${MAX_DEFAULT_SEARCH_KEYWORDS.toString()} search keywords — remove one to add this`;
    }
    return "Highlight only — click to make it a search keyword";
  };

  /* Handlers */
  const handleAddKeyword = () => {
    const keyword = keywordInput.trim();
    const isDuplicate = keywords.some(
      (existingKeyword) =>
        existingKeyword.keyword.toLowerCase() === keyword.toLowerCase(),
    );
    if (!keyword || isDuplicate || isAtKeywordLimit) {
      setKeywordInput("");
      return;
    }

    onChange([
      ...keywords,
      { category: null, color: null, isDefaultSearchKeyword: false, keyword },
    ]);
    setKeywordInput("");
  };

  const handleKeywordInputKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key !== "Enter" && event.key !== ",") return;

    event.preventDefault();
    handleAddKeyword();
  };

  const handleToggleSearchKeyword = (keyword: JobKeyword) => {
    // Toggling off is always allowed; toggling on is blocked at the cap.
    if (!keyword.isDefaultSearchKeyword && isAtSearchKeywordLimit) return;
    onChange(
      keywords.map((existingKeyword) =>
        existingKeyword.keyword === keyword.keyword
          ? {
              ...existingKeyword,
              isDefaultSearchKeyword: !existingKeyword.isDefaultSearchKeyword,
            }
          : existingKeyword,
      ),
    );
  };

  const handleRemoveKeyword = (keyword: JobKeyword) => {
    onChange(
      keywords.filter(
        (existingKeyword) => existingKeyword.keyword !== keyword.keyword,
      ),
    );
  };

  /* Render */
  const renderGenerateButton = () => {
    if (!onGenerate) return null;
    if (isGenerating) {
      return (
        <Row className="items-center gap-2">
          <SparklesIcon className="h-3.5 w-3.5 animate-pulse text-blue-600" />
          <LoadingIndicator variant="small" />
        </Row>
      );
    }

    return (
      <Button
        className="h-7 rounded-full border-blue-200 bg-blue-50 px-2.5 text-xs font-medium text-blue-700 hover:bg-blue-100 hover:text-blue-800"
        onClick={onGenerate}
        size="sm"
        type="button"
        variant="outline"
      >
        <SparklesIcon className="h-3.5 w-3.5" />
        {keywords.length
          ? "Regenerate from qualifications & top priority"
          : "Generate"}
      </Button>
    );
  };

  const renderKeywordChip = (keyword: JobKeyword) => {
    return (
      <Row
        className={cn(
          "items-center rounded-full border pr-1 transition-colors",
          keyword.isDefaultSearchKeyword
            ? "border-primary-900/30 bg-primary-100 text-primary-900 hover:bg-primary-200"
            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
        )}
        key={keyword.keyword}
      >
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="h-7 gap-1.5 rounded-full bg-transparent pl-2.5 pr-1.5 text-xs font-medium text-current hover:bg-transparent"
                onClick={() => {
                  handleToggleSearchKeyword(keyword);
                }}
                size="sm"
                type="button"
                variant="ghost"
              >
                {keyword.isDefaultSearchKeyword ? (
                  <SearchIcon className="h-3 w-3 shrink-0 text-primary-900" />
                ) : null}
                {keyword.keyword}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{getToggleTooltip(keyword)}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Button
          className="h-5 w-5 shrink-0 bg-transparent p-0 text-slate-400 hover:bg-black/10 hover:text-slate-600"
          onClick={() => {
            handleRemoveKeyword(keyword);
          }}
          size="icon"
          title={`Remove ${keyword.keyword}`}
          type="button"
          variant="ghost"
        >
          <XIcon className="h-3 w-3" />
        </Button>
      </Row>
    );
  };

  const renderKeywords = () => {
    if (!keywords.length) {
      return (
        <Text className="text-sm font-normal italic text-slate-400">
          No keywords yet.
        </Text>
      );
    }

    return (
      <Row className="flex-wrap gap-2">{keywords.map(renderKeywordChip)}</Row>
    );
  };

  const render = () => {
    return (
      <Column className="gap-3">
        <Row className="items-center justify-between gap-3">
          <Text className="text-sm font-medium text-slate-700">
            Search defaults {searchKeywordCount}/{MAX_DEFAULT_SEARCH_KEYWORDS} ·{" "}
            {keywords.length} total
          </Text>
          {renderGenerateButton()}
        </Row>
        {renderKeywords()}
        <Input
          className="max-w-sm"
          disabled={isAtKeywordLimit}
          onBlur={handleAddKeyword}
          onChange={(event) => {
            setKeywordInput(event.target.value);
          }}
          onKeyDown={handleKeywordInputKeyDown}
          placeholder={
            isAtKeywordLimit
              ? `Max ${MAX_JOB_KEYWORDS.toString()} keywords`
              : "Add a keyword and press Enter"
          }
          value={keywordInput}
        />
      </Column>
    );
  };

  return render();
};
